<script setup lang="ts">
import MinersConsolidationGridColumn, {
  type IConsolidationPartsDict,
} from '@/components/rc/miner-repairs/consolidation/MinersConsolidationGridColumn.vue'
import { ConsolidationAction, type IConsolidationAction } from '@/types/rc/miners/forms'
import { IndividualPart, IndividualPartToNumber, partIcons, partTypes, PartType } from '@/types/rc/miners/PartType'
import { Icon } from '@iconify/vue'
import { promiseTimeout } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { range, fromPairs, compact, isEqual, type Dictionary } from 'lodash'
import { computed, ref, useTemplateRef } from 'vue'

defineProps<{ preset: (number | undefined)[]; workOrderNumber: string; customerId: string }>()

const partsIndex = Object.values(IndividualPart).filter((individualPart) => individualPart !== IndividualPart.Miner)

const emit = defineEmits(['actionLoopDetected'])

// Swaps and movements made, post to BE on save
const actions = defineModel<IConsolidationAction[]>({ required: true })

// Model for parts in the grid
const minerParts = ref<(IConsolidationPartsDict | undefined)[]>([undefined, undefined, undefined])

// Logic to to remove cycles in actions
// Snapshots of the state of the grid state at each momentm, an array of observed loops,
// and the derived list of actions without the loops.
type IConsolidationGridLayout = (Dictionary<number> | undefined)[][]
const stateSnapshots = ref<IConsolidationGridLayout>([])
const actionLoops = ref<[number, number][]>([])

// Clean the loops out
const cleanActions = computed(() => {
  if (actionLoops.value.length == 0) return actions.value
  const clean = []
  let head = 0
  for (const [start, end] of actionLoops.value) {
    clean.push(...actions.value.slice(head, start))
    head = end + 1
  }
  clean.push(...actions.value.slice(head))
  return clean
})

// Keep a reference to the selected miners ids to post to BE
const minerIds = computed(() => gridColumns.value?.map((column) => column.resolvedId) ?? [])

// Selected miners ids, excluding undefined gaps, to pass into the dropdown's exclude prop
const alreadyUsedMinerIds = computed(() => compact(minerIds.value))

// Whether to display the left index column
const anyMinerData = computed(() => minerParts.value.some((parts) => parts && Object.keys(parts).length > 0))

const YELLOW = '#ffbe17'
const VIOLET = '#916aff'
const BLUE = '#0974f1'
const COLORS = [YELLOW, VIOLET, BLUE]

interface ITargetPartPayload {
  partName: IndividualPart
  column: number
}

// Buffers for drag origin and destination
const dragOriginBuffer = ref<ITargetPartPayload>()
const dragDestinationBuffer = ref<ITargetPartPayload>()

// Computed to dim the inactive part type slots when any part is being dragged (activePartType is set)
const activePartType = computed(() =>
  dragOriginBuffer.value?.partName ? partTypes[dragOriginBuffer.value?.partName] : undefined
)

// Drag start
function onDragStart(partName: IndividualPart, column: number) {
  dragOriginBuffer.value = { partName, column }
}

// Drag unsnap
function onDragUnsnap() {
  dragDestinationBuffer.value = undefined
}

// Drag snap
function onDragSnap(partName: IndividualPart, column: number) {
  dragDestinationBuffer.value = { partName, column }
}

// Drag end
async function onDragEnd() {
  // This event is called prior to drop event, which is not called on drop outside of a container
  await promiseTimeout(100)
  dragOriginBuffer.value = undefined
  dragDestinationBuffer.value = undefined
}

// Drag drop
function onDragCommit() {
  if (!dragOriginBuffer.value || !dragDestinationBuffer.value || !minerParts.value) {
    dragOriginBuffer.value = undefined
    dragDestinationBuffer.value = undefined
    return
  }

  const originColumn = dragOriginBuffer.value.column - 1
  const destinationColumn = dragDestinationBuffer.value.column - 1
  const partType = partTypes[dragOriginBuffer.value.partName]

  // Typecheck formality, they'll be there since no containers are rendered without parts
  if (!minerParts.value[originColumn] || !minerParts.value[destinationColumn]) return

  // No dragging into the same slot
  if (originColumn == destinationColumn && dragOriginBuffer.value.partName == dragDestinationBuffer.value.partName)
    return

  // Both miner ids should be present at this point
  // (so far they've been missing only upon dev server reload)
  if (!minerIds.value[originColumn] || !minerIds.value[destinationColumn]) {
    ElMessage.error(
      `Unexpected missing minerId for miner ${originColumn} (${minerIds.value[originColumn]}) or miner ${destinationColumn} (${minerIds.value[destinationColumn]}). Please report.`
    )
    return
  }

  // Grab origin and destination parts
  const originPart = minerParts.value[originColumn][dragOriginBuffer.value.partName]
  const destinationPart = minerParts.value[destinationColumn][dragDestinationBuffer.value.partName]

  // Tell which action corresponds
  const action = originPart.id && destinationPart.id ? ConsolidationAction.Swap : ConsolidationAction.Movement

  // If it's a Fan or HB movement, the destination part should have a part number... it should never happen it doesn't
  if (
    action == ConsolidationAction.Movement &&
    (partType == PartType.Fan || partType == PartType.Hashboard) &&
    IndividualPartToNumber[dragDestinationBuffer.value.partName] === null
  ) {
    ElMessage.error(
      `Unexpected missing part number for ${dragDestinationBuffer.value.partName} @ column ${destinationColumn + 1}. Please report.`
    )
    return
  }

  // Base common payload for both actions
  const basePayload = {
    undo: {
      left_column: originColumn,
      left_part_name: dragOriginBuffer.value.partName,
      right_column: destinationColumn,
      right_part_name: dragDestinationBuffer.value.partName,
    },
    part_type: partTypes[dragOriginBuffer.value.partName],
    left_miner_id: minerIds.value[originColumn]!,
    left_part_id: originPart.id!,
    right_miner_id: minerIds.value[destinationColumn]!,
  }

  // If it's the first action, snapshot the initial state
  if (stateSnapshots.value.length == 0) {
    stateSnapshots.value.push(consolidationSnapshot(minerParts.value))
  }

  // Actual UI swap
  minerParts.value[originColumn][dragOriginBuffer.value.partName] = destinationPart
  minerParts.value[destinationColumn][dragDestinationBuffer.value.partName] = originPart

  // Actual payload, segregated by action
  const payload: IConsolidationAction =
    action == ConsolidationAction.Swap
      ? { ...basePayload, action, right_part_id: destinationPart.id }
      : {
          ...basePayload,
          action,
          right_part_number: IndividualPartToNumber[dragDestinationBuffer.value.partName] ?? undefined,
        }

  actions.value.push(payload)

  // Snapshot the state
  const currentState = consolidationSnapshot(minerParts.value)

  // Check for loops - if the current state is already in the snapshots, outside of existing loop states, we have a new loop
  const loopStart = stateSnapshots.value.findIndex((snapshot) => isEqual(snapshot, currentState))
  if (loopStart != -1 && !actionLoops.value.some(([start, end]) => loopStart > start && loopStart <= end)) {
    emit('actionLoopDetected')
    actionLoops.value.push([loopStart, stateSnapshots.value.length - 1])
  }

  // Push the current state
  stateSnapshots.value.push(currentState)

  // Clear the buffers
  dragOriginBuffer.value = undefined
  dragDestinationBuffer.value = undefined
}

// Undo the last action
const undoAction = () => {
  const lastAction = actions.value.pop()!

  if (!lastAction || !minerParts.value || !lastAction.undo) return

  const undoPayload = lastAction.undo

  const originColumn = undoPayload.left_column
  const originPartName = undoPayload.left_part_name
  const destinationColumn = undoPayload.right_column
  const destinationPartName = undoPayload.right_part_name

  // Swap back the parts
  const originPart = minerParts.value[originColumn]![originPartName]
  const destinationPart = minerParts.value[destinationColumn]![destinationPartName]
  minerParts.value[destinationColumn]![destinationPartName] = originPart
  minerParts.value[originColumn]![originPartName] = destinationPart

  // Remove the last state snapshot
  stateSnapshots.value.pop()

  // Remove the last loop if it was the last action
  if (actionLoops.value.length > 0 && actionLoops.value[actionLoops.value.length - 1][1] == actions.value.length) {
    actionLoops.value.pop()
  }
}

// This is the proper way of dealing with refs arrays. Thanks you Vue 3, you're awesome.
const gridColumns = useTemplateRef<InstanceType<typeof MinersConsolidationGridColumn>[]>('gridColumns')

const load = () => {
  // Load the columns
  gridColumns.value?.forEach((column) => column.load())
}

const reset = () => {
  // Reset the buffers
  dragOriginBuffer.value = undefined
  dragDestinationBuffer.value = undefined

  // Reset the actions
  actions.value = []
  stateSnapshots.value = []
  actionLoops.value = []

  // Reset the columns
  gridColumns.value?.forEach((column) => column.reset())
}

const clear = () => {
  // Reset the buffers
  dragOriginBuffer.value = undefined
  dragDestinationBuffer.value = undefined

  // Clear the actions
  actions.value = []
  stateSnapshots.value = []
  actionLoops.value = []

  // Clear the columns
  gridColumns.value?.forEach((column) => column.clear())
}

defineExpose({ reset, clear, load, cleanActions, undoAction })

/**
 * Takes a snapshot of the current state of the miner parts, retaining only the part ids
 * @param minersParts the consolidation grid current state
 */
const consolidationSnapshot = (minersParts: (IConsolidationPartsDict | undefined)[]) =>
  minersParts.map((minerParts) =>
    minerParts ? fromPairs(partsIndex.map((partName) => [partName, minerParts[partName]?.id])) : undefined
  )
</script>

<template>
  <div class="grid grid-cols-[120px_1fr_1fr_1fr] gap-4 select-none">
    <!-- Index column -->
    <span class="flex items-center gap-2 font-extralight col-start-1 row-start-2">
      <icon :icon="partIcons[partTypes[IndividualPart.Miner]]" /> Miner
    </span>
    <template v-if="anyMinerData">
      <span
        v-for="(part, i) in partsIndex"
        :key="part"
        class="flex items-center gap-2 font-extralight col-start-1"
        :style="`grid-row-start: ${i + 3}`"
      >
        <icon :icon="partIcons[partTypes[part]]" />
        {{ part }}
      </span>
    </template>
    <!-- One column per miner -->
    <MinersConsolidationGridColumn
      v-for="i in range(3)"
      :key="i"
      ref="gridColumns"
      v-model="minerParts[i]"
      :column="i + 1"
      :color="COLORS[i]"
      :prevent-refetch="actions.length > 0"
      :preset="preset[i]"
      :exclude="alreadyUsedMinerIds"
      :active="activePartType"
      :work-order-number="workOrderNumber"
      :customer-id="customerId"
      @drag-start="onDragStart"
      @drag-snap="onDragSnap"
      @drag-unsnap="onDragUnsnap"
      @drag-end="onDragEnd"
      @drop="onDragCommit"
      @miner-selected="reset"
    />
  </div>
</template>
